import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { getApiErrorMessage } from '../../utils/apiError';

// Types mirror GET /api/groups/:id/grid (see docs/specs/journal-grid.md)

export interface GridVacationType {
  id: number;
  name: string;
  isIllness: boolean;
  isFreeze: boolean;
}

export interface GridVacation {
  id: number;
  dateFrom: string;
  dateTo: string;
  vacationType?: GridVacationType;
}

export interface GridSubscription {
  id: number;
  startDate: string;
  effectiveEndDate: string | null;
  lessonsCount: number;
  pricePerLesson: string;
  totalAmount: string;
  paymentDate: string;
  consumedCount: number;
  isFreeze: boolean;
  typeName: string | null;
}

/** Membership window [from, to) — `to` is exclusive; null = unbounded */
export interface GridMembershipWindow {
  from: string | null;
  to: string | null;
}

export interface GridStudent {
  id: number;
  firstName: string;
  lastName: string;
  surname?: string | null;
  quitDate: string | null;
  isFormer: boolean;
  memberships: GridMembershipWindow[];
  vacations: GridVacation[];
  subscriptions: GridSubscription[];
}

export interface GridLesson {
  id: number;
  date: string;
  topic: string | null;
  canceled: boolean;
  absentStudentIds: number[];
}

export interface GridTeacherVacation {
  id: number;
  dateFrom: string;
  dateTo: string;
}

export interface GroupGridData {
  group: {
    id: number;
    name: string | null;
    level: string;
    endDate: string | null;
    schedule: { dayOfWeek: string; time: string }[];
  };
  range: { from: string; to: string; today: string };
  students: GridStudent[];
  lessons: GridLesson[];
  ghostDates: string[];
  teacherVacations: GridTeacherVacation[];
}

export interface JournalState {
  grid: GroupGridData | null;
  /** Initial load of a group (grid replaced by spinner) */
  loading: boolean;
  /** Background refetch after a mutation (grid stays mounted) */
  refreshing: boolean;
  error: string | null;
}

const initialState: JournalState = {
  grid: null,
  loading: false,
  refreshing: false,
  error: null,
};

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

const getAuthHeaders = (getState: () => unknown) => {
  const state = getState() as { auth?: { token?: string | null } };
  const token = state.auth?.token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const fetchGroupGridAsync = createAsyncThunk(
  'journal/fetchGroupGrid',
  async (
    { groupId }: { groupId: number; silent?: boolean },
    { rejectWithValue, getState }
  ) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/groups/${groupId}/grid`, {
        headers: getAuthHeaders(getState),
      });
      return response.data as GroupGridData;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to load journal'));
    }
  }
);

const journalSlice = createSlice({
  name: 'journal',
  initialState,
  reducers: {
    clearJournal(state) {
      state.grid = null;
      state.error = null;
      state.loading = false;
      state.refreshing = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGroupGridAsync.pending, (state, action) => {
        if (action.meta.arg.silent && state.grid) {
          state.refreshing = true;
        } else {
          state.loading = true;
          state.grid = null;
        }
        state.error = null;
      })
      .addCase(fetchGroupGridAsync.fulfilled, (state, action) => {
        state.grid = action.payload;
        state.loading = false;
        state.refreshing = false;
      })
      .addCase(fetchGroupGridAsync.rejected, (state, action) => {
        state.loading = false;
        state.refreshing = false;
        state.error = (action.payload as string) || 'Failed to load journal';
      });
  },
});

export const { clearJournal } = journalSlice.actions;
export default journalSlice.reducer;
